import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { BarrasPorMes, Ranking } from "../componentes/PeritoGraficos";
import { Alternador, Secao } from "../componentes/PeritoUi";
import { formatarData } from "../lib/perito/datas";
import { serieMensal } from "../lib/perito/financeiro";
import { formatBRL, plural, type Tom } from "../lib/perito/format";
import { PERIODOS_DA_INTELIGENCIA, ROTULO_DO_PERIODO, calcularInteligencia, periodoDaBusca, type Lider } from "../lib/perito/inteligencia";
import { emLista } from "../lib/perito/despacho";
import { usePerito } from "../lib/perito/store";
import type { Pericia } from "../lib/perito/tipos";
import { nomeDaPericia } from "./PeritoPericias";

// A Inteligência pericial da Central do Perito (fase C), portada de `src/routes/_authenticated/inteligencia.tsx`:
// nove perguntas que o perito faz sobre a própria operação, o ranking dos tribunais e os recebimentos por mês. O
// período fica no ENDEREÇO (`?periodo=30`). As contas são de `lib/perito/inteligencia.ts`, provadas em
// `tests/perito-relatorios.mjs`; a tela não calcula nada.
//
// O que muda de lá na tela: o empate é dito ("empatado com TRT-2"), as perícias atrasadas e paradas aparecem
// pelo nome, com o link para a ficha (lá era só o número), e o ranking tem todos os tribunais.

const RAIZ = "/ferramenta/central-do-perito";
const OPCOES = PERIODOS_DA_INTELIGENCIA.map((id) => ({ id, rotulo: id === "ano" ? "12 meses" : `${id} dias` }));

const COR_DO_TOM: Record<Exclude<Tom, "info">, string> = {
  neutro: "text-titulo",
  ok: "text-aviso-tinta",
  atencao: "text-alerta-tinta",
  erro: "text-erro-tinta",
};

/** Uma pergunta e a resposta que os dados dão — ou o que falta para ela existir. */
function Pergunta({ nome, pergunta, resposta, detalhe, tom = "neutro", vazio, children }: {
  nome: string; pergunta: string; resposta?: string; detalhe?: ReactNode; tom?: Exclude<Tom, "info">; vazio?: string; children?: ReactNode;
}) {
  return (
    <div className="surface rise flex min-w-0 flex-col gap-2 p-5" data-pergunta={nome}>
      <h2 className="text-sm leading-snug font-semibold text-cinza">{pergunta}</h2>
      {resposta ? (
        <>
          <div className={`font-display text-xl leading-tight font-bold break-words tabular-nums ${COR_DO_TOM[tom]}`} data-resposta>{resposta}</div>
          {detalhe && <p className="text-xs text-cinza" data-detalhe>{detalhe}</p>}
          {children}
        </>
      ) : (
        <p className="text-sm leading-relaxed text-cinza" data-vazio>{vazio ?? "Ainda sem dados suficientes."}</p>
      )}
    </div>
  );
}

/** "empatado com TRT-2", quando há empate; senão nada. */
const empate = (l: Lider | null) => (l && l.empatados.length ? ` · empatado com ${emLista(l.empatados)}` : "");

/** As perícias pelo nome, com o link para a ficha: as 3 primeiras, e quantas mais. */
function ListaDePericias({ pericias }: { pericias: Pericia[] }) {
  if (pericias.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 text-sm" data-lista-de-pericias>
      {pericias.slice(0, 3).map((p) => (
        <li key={p.id} className="truncate">
          <Link to={`${RAIZ}/pericias/${p.id}`} className="font-semibold text-link hover:underline">{nomeDaPericia(p)}</Link>
        </li>
      ))}
      {pericias.length > 3 && <li className="text-xs text-cinza">e mais {pericias.length - 3}</li>}
    </ul>
  );
}

export default function PeritoInteligencia() {
  const s = usePerito();
  const [query, setQuery] = useSearchParams();
  const periodo = periodoDaBusca(query.get("periodo"));
  const mudarPeriodo = (p: string) => {
    const nova = new URLSearchParams(query);
    if (p === "90") nova.delete("periodo");
    else nova.set("periodo", p);
    setQuery(nova, { replace: true });
  };
  const i = useMemo(() => calcularInteligencia(s.pericias, s.historico, periodo), [s.pericias, s.historico, periodo]);
  const serie = useMemo(() => serieMensal(s.pericias, 12), [s.pericias]);
  const semRecebimento = serie.every((m) => m.recebido === 0 && m.previsto === 0);

  return (
    <div className="flex flex-col gap-5" data-inteligencia data-periodo={periodo}>
      <Cabine rotulo="Gestão" titulo="Inteligência pericial"
        descricao={<>Respostas diretas sobre a sua operação: {ROTULO_DO_PERIODO[periodo].toLowerCase()}, de {formatarData(i.janela.de)} a {formatarData(i.janela.ate)}.</>}
        acao={s.pericias.length > 0 ? <Alternador rotulo="Período" opcoes={OPCOES} valor={periodo} aoMudar={mudarPeriodo} /> : undefined} />

      {s.pericias.length === 0 ? (
        <div className="surface p-6 text-sm text-cinza" data-sem-pericias>
          Ainda não há perícias cadastradas. As respostas aparecem depois do primeiro cadastro.{" "}
          {s.podeEscrever && <Link to={`${RAIZ}/pericias/nova`} className="font-semibold text-link hover:underline">Cadastrar uma perícia</Link>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Pergunta nome="tribunal-nomeia" pergunta="Qual tribunal mais me nomeia?" resposta={i.tribunalMaisNomeia?.nome}
              detalhe={i.tribunalMaisNomeia && `${plural(i.tribunalMaisNomeia.valor, "nomeação", "nomeações")} no período${empate(i.tribunalMaisNomeia)}`}
              vazio="Nenhuma nomeação neste período. A data da nomeação, ou do cadastro, situa a perícia." />
            <Pergunta nome="tribunal-receita" pergunta="Qual tribunal gera mais receita?" tom="ok" resposta={i.tribunalMaisReceita?.nome}
              detalhe={i.tribunalMaisReceita && `${formatBRL(i.tribunalMaisReceita.valor)} recebidos no período${empate(i.tribunalMaisReceita)}`}
              vazio="Nenhum recebimento neste período." />
            <Pergunta nome="vara" pergunta="Qual vara é mais lucrativa?" tom="ok" resposta={i.varaMaisLucrativa?.nome}
              detalhe={i.varaMaisLucrativa && `${formatBRL(i.varaMaisLucrativa.valor)} recebidos no período${empate(i.varaMaisLucrativa)}`}
              vazio="Informe a vara nas perícias e os recebimentos para ver este indicador." />
            <Pergunta nome="tipo" pergunta="Qual tipo de perícia é mais rentável?" resposta={i.tipoMaisRentavel?.nome}
              detalhe={i.tipoMaisRentavel && `Honorário médio de ${formatBRL(i.tipoMaisRentavel.valor)}, nas perícias nomeadas no período${empate(i.tipoMaisRentavel)}`}
              vazio="Lance os honorários nas perícias nomeadas no período para comparar os tipos." />
            <Pergunta nome="dias-ate-receber" pergunta="Quanto tempo entre o depósito e o recebimento?" tom="atencao"
              resposta={i.diasAteReceber ? (i.diasAteReceber.dias >= 0 ? plural(i.diasAteReceber.dias, "dia", "dias") : `${plural(-i.diasAteReceber.dias, "dia", "dias")} antes`) : undefined}
              detalhe={i.diasAteReceber && `Média de ${plural(i.diasAteReceber.amostra, "recebimento", "recebimentos")} do período com as duas datas`}
              vazio="Informe o vencimento e a data do recebimento dos honorários para ver este indicador." />
            <Pergunta nome="por-caso" pergunta="Quanto recebi por caso, em média?" tom="ok" resposta={i.recebidoPorCaso ? formatBRL(i.recebidoPorCaso.valor) : undefined}
              detalhe={i.recebidoPorCaso && `Sobre ${plural(i.recebidoPorCaso.casos, "perícia", "perícias")} com recebimento no período`}
              vazio="Nenhum recebimento neste período." />
            <Pergunta nome="receita-12-meses" pergunta="Quanto recebi nos últimos 12 meses?" tom="ok"
              resposta={i.receitaDe12Meses.valor > 0 ? formatBRL(i.receitaDe12Meses.valor) : undefined}
              detalhe={`A soma do gráfico abaixo, de ${i.receitaDe12Meses.de.split("-").reverse().join("/")} a ${i.receitaDe12Meses.ate.split("-").reverse().join("/")}`}
              vazio="Nenhum recebimento nos últimos 12 meses." />
            <Pergunta nome="atrasadas" pergunta="Quais perícias estão atrasadas?" tom={i.atrasadas.pericias.length ? "erro" : "ok"}
              resposta={String(i.atrasadas.pericias.length)}
              detalhe={i.atrasadas.pericias.length ? `de ${plural(i.atrasadas.ativas, "perícia em andamento", "perícias em andamento")}, com a entrega do laudo antes de hoje` : "Nenhuma perícia em andamento com a entrega do laudo vencida."}>
              <ListaDePericias pericias={i.atrasadas.pericias} />
            </Pergunta>
            <Pergunta nome="paradas" pergunta="Quais perícias estão sem movimentação?" tom={i.semMovimentacao.length ? "atencao" : "ok"}
              resposta={String(i.semMovimentacao.length)}
              detalhe={i.semMovimentacao.length ? "em andamento, sem mudança nem registro no histórico há mais de 30 dias" : "Todas as perícias em andamento tiveram movimento nos últimos 30 dias."}>
              <ListaDePericias pericias={i.semMovimentacao} />
            </Pergunta>
          </div>

          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
            <Secao nome="ranking-de-tribunais" titulo="Tribunais que mais nomeiam" descricao={`${ROTULO_DO_PERIODO[periodo]}, todos os tribunais`}>
              {i.rankingDeTribunais.length === 0
                ? <p className="text-sm text-cinza">Nenhuma nomeação neste período.</p>
                : <Ranking itens={i.rankingDeTribunais.map((t) => ({ nome: t.nome, valor: t.quantas }))} />}
            </Secao>
            <Secao nome="recebimentos" titulo="Recebimentos por mês" descricao="Os últimos 12 meses: o que vence e o que entrou em cada um">
              {semRecebimento ? <p className="text-sm text-cinza">Ainda não há honorários com data nos últimos 12 meses.</p> : <BarrasPorMes serie={serie} />}
            </Secao>
          </div>
        </>
      )}
    </div>
  );
}
